import { gsap } from 'gsap';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin(InertiaPlugin);

/**
 * Main navigation menu functionality
 */
export default class MainNavigation {
    constructor() {
        // Animation constants
        this.ANIMATION_CONFIG = {
            HEIGHT_DURATION: 0.8,
            HEIGHT_EASE: "elastic.out(0.7, 0.9)", // Very subtle bounce
            CLOSE_DURATION: 0.6,
            CLOSE_EASE: "power2.out", // No overshoot to avoid height bounce on close
            OPACITY_DURATION: 0.5,
            OPACITY_DELAY: 0.2,
            ARROW_DURATION: 0.4,
            ARROW_EASE: "elastic.out(0.7, 0.9)", // Subtle bounce for arrow
            CLOSE_DELAY: 0.05,
            DEBOUNCE_TIME: 100 // Shorter debounce for accidental double-clicks
        };

        this.menuContainer = document.querySelector('.menu-desktop');
        this.submenus = new Map();
        this.searchMenu = null;
        this.currentOpenSubmenu = null;
        this.currentOpenSearch = false;
        this.originalHeight = null;
        this.lastClickTime = 0;
        this.resizeTimeout = null;
        this.isAnimating = false;
        this.isResizing = false;
        this.init();
    }

    init() {
        this.setupSubmenus();
        this.setupSearchMenu();
        this.bindEvents();
        this.waitForImagesAndSetHeight();
    }

    waitForImagesAndSetHeight() {
        // Ensure all images in header are loaded before calculating height
        const headerImages = this.menuContainer.querySelectorAll('img');
        const imagePromises = Array.from(headerImages).map(img => {
            return new Promise((resolve) => {
                if (img.complete) {
                    resolve();
                } else {
                    img.addEventListener('load', resolve);
                    img.addEventListener('error', resolve); // Also resolve on error to prevent hanging
                    // Fallback timeout to prevent indefinite waiting
                    setTimeout(resolve, 2000);
                }
            });
        });

        Promise.all(imagePromises).then(() => {
            // Add a small delay to ensure layout is stable
            requestAnimationFrame(() => {
                this.setOriginalHeight();
            });
        });
    }

    setOriginalHeight() {
        // Store the original height of the menu container
        const calculatedHeight = this.menuContainer.offsetHeight;

        // Defensive minimum height check (should be at least 60px for a typical header)
        const minimumHeight = 60;
        this.originalHeight = Math.max(calculatedHeight, minimumHeight);

        // Debug logging to help identify issues
        if (calculatedHeight < minimumHeight) {
            console.warn('Header height unusually small:', calculatedHeight, 'using minimum:', this.originalHeight);
        }
    }

    setupSubmenus() {
        // Find all submenu triggers
        const submenuTriggers = document.querySelectorAll('[data-submenu-trigger]');

        submenuTriggers.forEach(trigger => {
            const submenuId = trigger.getAttribute('data-submenu-trigger');
            const submenu = document.getElementById(submenuId);

            if (submenu && trigger) {
                // Cache DOM elements
                const arrow = trigger.querySelector('.submenu-icon');

                // Initialize submenu state
                gsap.set(submenu, {
                    opacity: 0
                });

                this.submenus.set(submenuId, {
                    trigger,
                    submenu,
                    arrow, // Cache arrow element
                    isOpen: false,
                    calculatedHeight: null, // Cache calculated height
                    timeline: null // Track animation timeline
                });

                // Set initial ARIA attributes
                trigger.setAttribute('aria-expanded', 'false');
                submenu.setAttribute('aria-hidden', 'true');
                submenu.setAttribute('inert', '');
            }
        });
    }

    setupSearchMenu() {
        // Find search trigger and menu
        const searchTrigger = document.querySelector('[data-search-trigger]');
        const searchMenu = document.getElementById('search-menu');

        if (searchTrigger && searchMenu) {
            // Initialize search menu state
            gsap.set(searchMenu, {
                opacity: 0
            });

            this.searchMenu = {
                trigger: searchTrigger,
                menu: searchMenu,
                isOpen: false,
                calculatedHeight: null,
                timeline: null
            };

            // Set initial ARIA attributes
            searchTrigger.setAttribute('aria-expanded', 'false');
            searchMenu.setAttribute('aria-hidden', 'true');
            searchMenu.setAttribute('inert', '');
        }
    }

    bindEvents() {
        // Handle submenu trigger clicks
        this.submenus.forEach((submenuData, submenuId) => {
            const { trigger } = submenuData;

            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleSubmenuToggle(submenuId);
            });
        });

        // Handle search trigger click
        if (this.searchMenu) {
            this.searchMenu.trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleSearchToggle();
            });
        }

        // Close submenu/search when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.menuContainer.contains(e.target)) {
                if (this.currentOpenSubmenu) {
                    this.closeSubmenu(this.currentOpenSubmenu);
                }
                if (this.currentOpenSearch) {
                    this.closeSearchMenu();
                }
            }
        });

        // Handle escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if (this.currentOpenSubmenu) {
                    this.closeSubmenu(this.currentOpenSubmenu);
                }
                if (this.currentOpenSearch) {
                    this.closeSearchMenu();
                }
            }
        });

        // Handle window resize
        window.addEventListener('resize', () => {
            this.handleResize();
        });

        // Recalculate menu heights when header logo state changes
        window.addEventListener('header:scrolled-toggle', () => {
            // Defer to next frame to ensure DOM has applied logo size changes
            requestAnimationFrame(() => this.updateHeightsOnResize());
        });

        // Synchronize header height animation with logo toggle
        window.addEventListener('header:request-scroll-toggle', (e) => {
            // Let CSS handle everything; do not intercept
        });
    }


    handleSubmenuToggle(submenuId) {
        const submenuData = this.submenus.get(submenuId);
        if (!submenuData) return;

        // Prevent interaction during resize only (allow reversing mid-animation)
        if (this.isResizing) {
            return;
        }

        // Light debounce to prevent accidental double-clicks
        const now = Date.now();
        if (now - this.lastClickTime < this.ANIMATION_CONFIG.DEBOUNCE_TIME) {
            return;
        }
        this.lastClickTime = now;

        // Set animation state
        this.isAnimating = true;

        // Kill any existing timeline for this submenu to prevent conflicts
        if (submenuData.timeline) {
            submenuData.timeline.kill();
            submenuData.timeline = null;
        }

        // Allow reversing animation - toggle based on intended state, not current animation
        if (submenuData.isOpen) {
            this.closeSubmenu(submenuId);
            return;
        }

        // Close any other open submenu first
        if (this.currentOpenSubmenu && this.currentOpenSubmenu !== submenuId) {
            this.forceCloseSubmenu(this.currentOpenSubmenu);
        }

        // Close search menu if it is open to ensure only one dropdown is open
        if (this.currentOpenSearch) {
            this.forceCloseSearchMenu();
        }

        // Open the new submenu
        this.openSubmenu(submenuId);
    }

    handleSearchToggle() {
        if (!this.searchMenu) return;

        // Prevent interaction during resize only (allow reversing mid-animation)
        if (this.isResizing) {
            return;
        }

        // Light debounce to prevent accidental double-clicks
        const now = Date.now();
        if (now - this.lastClickTime < this.ANIMATION_CONFIG.DEBOUNCE_TIME) {
            return;
        }
        this.lastClickTime = now;

        // Set animation state
        this.isAnimating = true;

        // Kill any existing timeline for search menu
        if (this.searchMenu.timeline) {
            this.searchMenu.timeline.kill();
            this.searchMenu.timeline = null;
        }

        if (this.currentOpenSearch) {
            this.closeSearchMenu();
            return;
        }

        // Close any open submenu first, but preserve current height for smoother background transition
        const startingHeight = this.menuContainer.offsetHeight;
        if (this.currentOpenSubmenu) {
            this.hideSubmenuWithoutResizing(this.currentOpenSubmenu);
        }

        // Open search menu
        this.openSearchMenu(startingHeight);
    }

    /**
     * Immediately close the search menu without animation.
     * Ensures clean state before opening another dropdown.
     */
    forceCloseSearchMenu() {
        if (!this.searchMenu) return;
        const { trigger, menu } = this.searchMenu;
        if (this.searchMenu.timeline) {
            this.searchMenu.timeline.kill();
            this.searchMenu.timeline = null;
        }
        this.menuContainer.classList.remove('search-open');
        this.currentOpenSearch = false;
        gsap.set(menu, { display: 'none', opacity: 0 });
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        menu.setAttribute('aria-hidden', 'true');
        menu.setAttribute('inert', '');
        // Reset container height to base
        gsap.set(this.menuContainer, { height: this.originalHeight + 'px' });
    }

    /**
     * Immediately close a submenu without animation.
     */
    forceCloseSubmenu(submenuId) {
        const submenuData = this.submenus.get(submenuId);
        if (!submenuData) return;
        const { trigger, submenu, arrow } = submenuData;
        if (submenuData.timeline) {
            submenuData.timeline.kill();
            submenuData.timeline = null;
        }
        this.menuContainer.classList.remove('submenu-open');
        submenuData.isOpen = false;
        this.currentOpenSubmenu = null;
        gsap.set(submenu, { display: 'none', opacity: 0 });
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        submenu.setAttribute('aria-hidden', 'true');
        submenu.setAttribute('inert', '');
        if (arrow) gsap.set(arrow, { rotation: 0 });
        // Reset container height to base
        gsap.set(this.menuContainer, { height: this.originalHeight + 'px' });
    }

    /**
     * Hide submenu immediately without changing the container height.
     * Useful when transitioning to search to avoid height flicker.
     */
    hideSubmenuWithoutResizing(submenuId) {
        const submenuData = this.submenus.get(submenuId);
        if (!submenuData) return;
        const { trigger, submenu, arrow } = submenuData;
        if (submenuData.timeline) {
            submenuData.timeline.kill();
            submenuData.timeline = null;
        }
        this.menuContainer.classList.remove('submenu-open');
        submenuData.isOpen = false;
        this.currentOpenSubmenu = null;
        gsap.set(submenu, { display: 'none', opacity: 0 });
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        submenu.setAttribute('aria-hidden', 'true');
        submenu.setAttribute('inert', '');
        if (arrow) gsap.set(arrow, { rotation: 0 });
        // Intentionally do not modify menuContainer height here
    }

    openSubmenu(submenuId) {
        const submenuData = this.submenus.get(submenuId);
        if (!submenuData) return;

        const { trigger, submenu, arrow } = submenuData;

        // Ensure search is fully closed to avoid height inflation from cached values
        if (this.currentOpenSearch) {
            this.forceCloseSearchMenu();
        }

        // Set initial height to prevent jump
        gsap.set(this.menuContainer, { height: this.originalHeight + 'px' });

        // Show submenu to calculate height, but keep it invisible
        gsap.set(submenu, { display: 'block', opacity: 0 });

        // Calculate or use cached height
        let menuBarHeight;
        if (submenuData.calculatedHeight) {
            menuBarHeight = submenuData.calculatedHeight;
        } else {
            menuBarHeight = this.menuContainer.scrollHeight;
            // Defensive validation - ensure height is reasonable
            const minimumExpandedHeight = this.originalHeight + 100; // At least 100px expansion
            menuBarHeight = Math.max(menuBarHeight, minimumExpandedHeight);
            submenuData.calculatedHeight = menuBarHeight; // Cache for next time
        }

        // Add class to menu container
        this.menuContainer.classList.add('submenu-open');
        document.body.classList.add('nav-overlay-open');

        // Create animation timeline for better coordination
        const tl = gsap.timeline({
            onComplete: () => {
                submenuData.timeline = null;
                this.isAnimating = false;
            }
        });

        // Store timeline reference for potential cleanup
        submenuData.timeline = tl;

        // Animate menu container height expansion with elastic easing
        tl.to(this.menuContainer, {
            height: menuBarHeight,
            duration: this.ANIMATION_CONFIG.HEIGHT_DURATION,
            ease: this.ANIMATION_CONFIG.HEIGHT_EASE
        }, 0);

        // Animate submenu visibility
        tl.to(submenu, {
            opacity: 1,
            duration: this.ANIMATION_CONFIG.OPACITY_DURATION,
            ease: "power2.out"
        }, this.ANIMATION_CONFIG.OPACITY_DELAY);

        // Animate arrow rotation if it exists
        if (arrow) {
            tl.to(arrow, {
                rotation: 180,
                duration: this.ANIMATION_CONFIG.ARROW_DURATION,
                ease: this.ANIMATION_CONFIG.ARROW_EASE
            }, 0);
        }

        // Update states and ARIA attributes
        submenuData.isOpen = true;
        this.currentOpenSubmenu = submenuId;
        trigger.setAttribute('aria-expanded', 'true');
        submenu.removeAttribute('inert');
        submenu.setAttribute('aria-hidden', 'false');
    }

    closeSubmenu(submenuId) {
        const submenuData = this.submenus.get(submenuId);
        if (!submenuData) return;

        const { trigger, submenu, arrow } = submenuData;

        // Remove class from menu container
        this.menuContainer.classList.remove('submenu-open');
        document.body.classList.remove('nav-overlay-open');

        // Compute numeric start and end heights for a smooth close animation
        // 1) Current expanded height with submenu visible
        const currentHeight = this.menuContainer.scrollHeight;

        // 2) Temporarily hide submenu and set container to auto to measure base height without it
        gsap.set(submenu, { display: 'none' });
        gsap.set(this.menuContainer, { height: 'auto' });
        this.menuContainer.offsetHeight; // reflow
        const baseHeight = Math.max(this.menuContainer.offsetHeight, 60);
        // Restore submenu visibility for fade-out animation
        gsap.set(submenu, { display: 'block' });

        // 3) Set an explicit starting height so GSAP can animate (avoid 'auto')
        gsap.set(this.menuContainer, { height: currentHeight + 'px' });

        // Create animation timeline for better coordination
        const tl = gsap.timeline({
            onComplete: () => {
                submenuData.timeline = null;
                this.isAnimating = false;
                // Only hide submenu if it's actually closed
                if (!submenuData.isOpen) {
                    gsap.set(submenu, { display: 'none' });
                }
                // Allow natural height when closed for smoother logo transitions
                if (!this.currentOpenSubmenu && !this.currentOpenSearch) {
                    gsap.set(this.menuContainer, { height: 'auto' });
                }
            }
        });

        // Store timeline reference for potential cleanup
        submenuData.timeline = tl;

        // Animate submenu opacity out first
        tl.to(submenu, {
            opacity: 0,
            duration: 0.3,
            ease: "power2.in"
        }, 0);

        // Animate menu container height back to measured base height
        tl.to(this.menuContainer, {
            height: baseHeight + "px",
            duration: this.ANIMATION_CONFIG.CLOSE_DURATION,
            ease: this.ANIMATION_CONFIG.CLOSE_EASE
        }, this.ANIMATION_CONFIG.CLOSE_DELAY);

        // Animate arrow rotation back if it exists
        if (arrow) {
            tl.to(arrow, {
                rotation: 0,
                duration: this.ANIMATION_CONFIG.ARROW_DURATION,
                ease: this.ANIMATION_CONFIG.ARROW_EASE
            }, 0);
        }

        // Update states and ARIA attributes
        submenuData.isOpen = false;
        this.currentOpenSubmenu = null;
        trigger.setAttribute('aria-expanded', 'false');
        submenu.setAttribute('aria-hidden', 'true');
        submenu.setAttribute('inert', '');
    }

    openSearchMenu(startingHeight) {
        if (!this.searchMenu) return;

        const { trigger, menu } = this.searchMenu;

        // Ensure clean state and show search to calculate height
        const startHeightValue = Number.isFinite(startingHeight) ? startingHeight : (this.menuContainer?.offsetHeight || this.originalHeight);
        // Show search but measure natural expanded height first
        gsap.set(menu, { display: 'block', opacity: 0 });
        // Let container breathe to measure target height without constraint
        gsap.set(this.menuContainer, { height: 'auto' });
        this.menuContainer.offsetHeight; // reflow
        let menuBarHeight = this.menuContainer.scrollHeight;
        // Restore starting height for a proper animated transition
        gsap.set(this.menuContainer, { height: startHeightValue + 'px' });

        // Add class to menu container
        this.menuContainer.classList.add('search-open');
        document.body.classList.add('nav-overlay-open');

        // Create animation timeline
        const tl = gsap.timeline({
            onComplete: () => {
                this.searchMenu.timeline = null;
                this.isAnimating = false;
                // Focus the search input after animation
                const searchInput = menu.querySelector('input[type="search"]') || menu.querySelector('.search-field');
                if (searchInput) {
                    searchInput.focus();
                }
            }
        });

        // Store timeline reference
        this.searchMenu.timeline = tl;

        // Animate menu container height expansion
        tl.to(this.menuContainer, {
            height: menuBarHeight,
            duration: this.ANIMATION_CONFIG.HEIGHT_DURATION,
            ease: this.ANIMATION_CONFIG.HEIGHT_EASE
        }, 0);

        // Animate search menu visibility
        tl.to(menu, {
            opacity: 1,
            duration: this.ANIMATION_CONFIG.OPACITY_DURATION,
            ease: "power2.out",
            onStart: () => {
                // Make sure search is visible immediately on first open
                gsap.set(menu, { display: 'block' });
            }
        }, this.ANIMATION_CONFIG.OPACITY_DELAY);

        // Update states and ARIA attributes
        this.currentOpenSearch = true;
        trigger.setAttribute('aria-expanded', 'true');
        menu.removeAttribute('inert');
        menu.setAttribute('aria-hidden', 'false');
    }

    closeSearchMenu() {
        if (!this.searchMenu) return;

        const { trigger, menu } = this.searchMenu;

        // Remove class from menu container
        this.menuContainer.classList.remove('search-open');
        document.body.classList.remove('nav-overlay-open');

        // Compute numeric start and end heights for a smooth close animation
        const currentHeight = this.menuContainer.scrollHeight;
        // Temporarily hide search and set container to auto to measure base height
        gsap.set(menu, { display: 'none' });
        gsap.set(this.menuContainer, { height: 'auto' });
        this.menuContainer.offsetHeight;
        const baseHeight = Math.max(this.menuContainer.offsetHeight, 60);
        // Restore search visibility for fade-out
        gsap.set(menu, { display: 'block' });
        // Set explicit starting height
        gsap.set(this.menuContainer, { height: currentHeight + 'px' });

        // Create animation timeline
        const tl = gsap.timeline({
            onComplete: () => {
                this.searchMenu.timeline = null;
                this.isAnimating = false;
                if (!this.currentOpenSearch) {
                    gsap.set(menu, { display: 'none' });
                }
            }
        });

        // Store timeline reference
        this.searchMenu.timeline = tl;

        // Animate search menu opacity out first
        tl.to(menu, {
            opacity: 0,
            duration: 0.3,
            ease: "power2.in"
        }, 0);

        // Animate menu container height back to measured base height
        tl.to(this.menuContainer, {
            height: baseHeight + "px",
            duration: this.ANIMATION_CONFIG.CLOSE_DURATION,
            ease: this.ANIMATION_CONFIG.CLOSE_EASE
        }, this.ANIMATION_CONFIG.CLOSE_DELAY);

        // Update states and ARIA attributes
        this.currentOpenSearch = false;
        trigger.setAttribute('aria-expanded', 'false');
        menu.setAttribute('aria-hidden', 'true');
        menu.setAttribute('inert', '');
    }

    closeAllSubmenus() {
        if (this.currentOpenSubmenu) {
            this.closeSubmenu(this.currentOpenSubmenu);
        }
        if (this.currentOpenSearch) {
            this.closeSearchMenu();
        }
    }

    handleResize() {
        // Prevent resize handling during animations
        if (this.isAnimating) {
            return;
        }

        // Set resize state
        this.isResizing = true;

        // Debounce resize events
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
            this.updateHeightsOnResize();
            this.isResizing = false;
        }, 250);
    }

    updateHeightsOnResize() {
        // Store previous state
        const wasSubmenuOpen = this.currentOpenSubmenu;
        const wasSearchOpen = this.currentOpenSearch;

        // Kill any running animations to prevent conflicts
        this.submenus.forEach(submenuData => {
            if (submenuData.timeline) {
                submenuData.timeline.kill();
                submenuData.timeline = null;
            }
        });
        if (this.searchMenu && this.searchMenu.timeline) {
            this.searchMenu.timeline.kill();
            this.searchMenu.timeline = null;
        }

        // Temporarily close any open menus to get accurate original height
        if (wasSubmenuOpen) {
            const submenuData = this.submenus.get(wasSubmenuOpen);
            gsap.set(submenuData.submenu, { display: 'none' });
        }
        if (wasSearchOpen && this.searchMenu) {
            gsap.set(this.searchMenu.menu, { display: 'none' });
        }

        // Reset container to auto height temporarily
        gsap.set(this.menuContainer, { height: 'auto' });

        // Force reflow to ensure layout is recalculated
        this.menuContainer.offsetHeight;

        // Recalculate original height with defensive minimum
        const calculatedHeight = this.menuContainer.offsetHeight;
        const minimumHeight = 60;
        this.originalHeight = Math.max(calculatedHeight, minimumHeight);

        // Clear all cached heights
        this.submenus.forEach(submenuData => {
            submenuData.calculatedHeight = null;
        });
        if (this.searchMenu) {
            this.searchMenu.calculatedHeight = null;
        }

        // If a submenu was open, recalculate and update its height
        if (wasSubmenuOpen) {
            const submenuData = this.submenus.get(wasSubmenuOpen);

            // Show submenu to recalculate height
            gsap.set(submenuData.submenu, { display: 'block' });

            // Force reflow and recalculate
            this.menuContainer.offsetHeight;
            let newHeight = this.menuContainer.scrollHeight;

            // Defensive validation
            const minimumExpandedHeight = this.originalHeight + 100;
            newHeight = Math.max(newHeight, minimumExpandedHeight);
            submenuData.calculatedHeight = newHeight;

            // Update the menu container height immediately
            gsap.set(this.menuContainer, { height: newHeight + 'px' });
        } else if (wasSearchOpen && this.searchMenu) {
            // Show search menu to recalculate height
            gsap.set(this.searchMenu.menu, { display: 'block' });

            // Force reflow and recalculate
            this.menuContainer.offsetHeight;
            let newHeight = this.menuContainer.scrollHeight;

            // Defensive validation
            const minimumExpandedHeight = this.originalHeight + 50;
            newHeight = Math.max(newHeight, minimumExpandedHeight);
            this.searchMenu.calculatedHeight = newHeight;

            // Update the menu container height immediately
            gsap.set(this.menuContainer, { height: newHeight + 'px' });
        } else {
            // Let header flow naturally when nothing is open (improves logo height changes)
            gsap.set(this.menuContainer, { height: 'auto' });
        }
    }

    /**
     * Clean up event listeners and references
     */
    destroy() {
        // Remove event listeners
        this.submenus.forEach((submenuData) => {
            const { trigger } = submenuData;
            if (trigger) {
                trigger.removeEventListener('click', this.handleSubmenuToggle);
            }
        });

        // Remove global event listeners
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleKeydown);

        // Clear references
        this.submenus.clear();
        this.menuContainer = null;
        this.currentOpenSubmenu = null;
    }
}